// One contact: fields, opt-out state with block/unblock, their conversations, a Text button.

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatPhone } from '@bt/core';
import { api, type MeResponse } from '../../api';
import { Loading, ErrorBox, Empty, InlineError } from '../../components/States';
import { UnblockDialog } from '../../components/UnblockDialog';
import { useToast } from '../../components/Toast';
import { displayName, fmtFull, fmtRelative, zoneLabel } from '../../lib/format';
import { COMMON_TIMEZONES, TZ_SOURCE_WORDS, effectiveTimezone } from '../../lib/timezone';
import { ReminderDialog } from '../scheduled/ReminderDialog';

export function ContactPage({ me }: { me: MeResponse }) {
  const { contactId = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['contact', contactId], queryFn: () => api.contacts.get(contactId), enabled: !!contactId });
  const [name, setName] = useState('');
  const [tz, setTz] = useState('');
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [unblock, setUnblock] = useState(false);
  const [reminder, setReminder] = useState(false);

  useEffect(() => {
    if (q.data) {
      setName(q.data.contact.name ?? '');
      setTz(q.data.contact.timezone ?? '');
      setAddress(q.data.contact.address ?? '');
      setNotes(q.data.contact.notes ?? '');
    }
  }, [q.data]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['contact', contactId] });
    qc.invalidateQueries({ queryKey: ['contacts'] });
    qc.invalidateQueries({ queryKey: ['conversations'] });
    qc.invalidateQueries({ queryKey: ['conversation'] });
  };
  const save = useMutation({
    mutationFn: () => api.contacts.update(contactId, { name: name.trim() || null, timezone: tz || null, address: address.trim() || null, notes: notes.trim() || null }),
    onSuccess: () => {
      refresh();
      toast({ text: 'Saved.', tone: 'ok' });
    },
  });
  const block = useMutation({
    mutationFn: () => api.contacts.optOut(contactId, 'block'),
    onSuccess: () => {
      refresh();
      toast({ text: 'Number blocked.', tone: 'ok' });
    },
  });
  const text = useMutation({
    mutationFn: () => api.conversations.create({ contact_id: contactId }),
    onSuccess: (r) => navigate(`/inbox/${r.conversation.id}`),
  });

  if (q.isPending) return <Loading />;
  if (q.isError)
    return (
      <div className="page narrow">
        <ErrorBox error={q.error} retry={() => q.refetch()} />
      </div>
    );

  const { contact, opted_out, opt_out, conversations } = q.data;
  const eff = effectiveTimezone(contact, me.workspace.timezone);
  const dirty = name !== (contact.name ?? '') || tz !== (contact.timezone ?? '') || address !== (contact.address ?? '') || notes !== (contact.notes ?? '');

  return (
    <div className="page narrow">
      <div className="page-head">
        <div>
          <div className="small">
            <Link to="/contacts">Contacts</Link> /
          </div>
          <h1>{displayName(contact)}</h1>
          <div className="muted">{formatPhone(contact.phone_number)}</div>
        </div>
        <div className="row">
          <button className="btn" onClick={() => setReminder(true)} disabled={opted_out}>
            Schedule reminder
          </button>
          <button className="btn primary" onClick={() => text.mutate()} disabled={text.isPending || opted_out} title={opted_out ? 'This number opted out' : undefined}>
            Text
          </button>
        </div>
      </div>
      <InlineError error={text.error} />

      <div className="card">
        {opted_out ? (
          <div className="row between wrap">
            <div>
              <span className="chip danger">OPTED OUT</span>
              <div className="small muted" style={{ marginTop: 4 }}>
                {opt_out ? `${opt_out.reason === 'stop_keyword' ? 'Replied STOP' : opt_out.reason === 'manual' ? 'Blocked by staff' : 'Blocked by the carrier'} · ${fmtFull(opt_out.created_at, me.workspace.timezone)}` : 'No texts go out to this number.'}
                {opt_out?.detail && <div>“{opt_out.detail}”</div>}
              </div>
            </div>
            <button className="btn" onClick={() => setUnblock(true)}>
              Unblock
            </button>
          </div>
        ) : (
          <div className="row between wrap">
            <span className="muted">Texting allowed</span>
            <button className="btn danger small" disabled={block.isPending} onClick={() => confirm(`Block ${formatPhone(contact.phone_number)}? No more texts will go out to this number.`) && block.mutate()}>
              Block this number
            </button>
          </div>
        )}
        <InlineError error={block.error} />
      </div>

      <div className="card">
        <h2>Details</h2>
        <label className="field">
          <span>Name</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="field">
          <span>Address</span>
          <input className="input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="123 Palm Ave, Fort Myers FL" />
          <div className="field-help">Where the service call goes. A list you type or upload fills this in for you.</div>
        </label>
        <label className="field">
          <span>Time zone</span>
          <select className="input" value={tz} onChange={(e) => setTz(e.target.value)}>
            <option value="">Automatic — {zoneLabel(effectiveTimezone({ ...contact, timezone: null }, me.workspace.timezone).tz)} ({TZ_SOURCE_WORDS[effectiveTimezone({ ...contact, timezone: null }, me.workspace.timezone).source]})</option>
            {COMMON_TIMEZONES.map((z) => (
              <option key={z.id} value={z.id}>
                {z.label}
              </option>
            ))}
          </select>
          <div className="field-help">
            Texting hours follow this zone. Now using {zoneLabel(eff.tz)} ({TZ_SOURCE_WORDS[eff.source]}).
          </div>
        </label>
        <label className="field">
          <span>Notes</span>
          <textarea className="input" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
        <InlineError error={save.error} />
        <button className="btn primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          Save
        </button>
      </div>

      <div className="card">
        <h2>Conversations</h2>
        {conversations.length === 0 ? (
          <Empty title="No conversations yet" hint="Press Text to start one." />
        ) : (
          <ul className="list-plain">
            {conversations.map((c) => (
              <li key={c.id}>
                <Link to={`/inbox/${c.id}`} className="row between">
                  <span className="grow" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.last_message ? `${c.last_message.direction === 'in' ? '← ' : '→ '}${c.last_message.body}` : 'No messages'}
                  </span>
                  <span className="muted small nowrap">
                    {c.status === 'closed' && <span className="chip">Closed</span>} {fmtRelative(c.last_message_at)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {unblock && <UnblockDialog contact={contact} onClose={() => setUnblock(false)} onDone={refresh} />}
      {reminder && <ReminderDialog me={me} contact={contact} onClose={() => setReminder(false)} />}
    </div>
  );
}
